// @ts-nocheck
import React from "react";
import { Box, Chip, TableCell, Tooltip, Typography } from "@mui/material";
import useStyles from "../DaikinConsistency.style";

export function applySortOrder(items, col, dir, valFn) {
  if (!col) { return items; }
  return [...items].sort((a, b) => {
    const av = valFn(a, col) ?? "";
    const bv = valFn(b, col) ?? "";
    const cmp =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return dir === "asc" ? cmp : -cmp;
  });
}

export const SortableHeader = ({ col, current, dir, onSort, className, sx, children }) => (
  <TableCell className={className} onClick={() => onSort(col)} sx={{ cursor: "pointer", userSelect: "none", ...sx }}>
    <Box display="flex" alignItems="center" gap={0.5}>
      <span>{children}</span>
      {current === col ? (
        <Typography variant="caption" color="primary.main" sx={{ lineHeight: 1, fontWeight: 700 }}>
          {dir === "asc" ? "↑" : "↓"}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1 }}>
          ⇅
        </Typography>
      )}
    </Box>
  </TableCell>
);

export const fmtDate = (val) => {
  if (!val) { return "—"; }
  const d = new Date(val);
  if (isNaN(d.getTime())) { return "—"; }
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export function getFlaggedFields(entity) {
  return new Set(entity?.flaggedFields || []);
}

const UI_ONLY_KEYS = new Set(["isVirtual", "flaggedFields"]);
const entityReplacer = (key, val) => (UI_ONLY_KEYS.has(key) ? undefined : val);

export function toEntityJson(entity) {
  return JSON.stringify(entity, entityReplacer, 2);
}

export function openEntityJson(entity) {
  const blob = new Blob([toEntityJson(entity)], { type: "application/json" });
  window.open(URL.createObjectURL(blob), "_blank");
}

export const IssueTooltipProps = {
  componentsProps: {
    tooltip: {
      sx: {
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: 3,
        maxWidth: "unset",
        width: "max-content",
        p: "20px",
      },
    },
    arrow: { sx: { color: "background.paper" } },
  },
};

export const IssueTooltipContent = ({ issues }) => (
  <Box>
    {issues.map((issue, i) => (
      <Typography
        key={i}
        variant="caption"
        display="block"
        sx={{ color: "text.primary", display: "flex", alignItems: "baseline", gap: 0.5 }}
      >
        <Box
          component="span"
          sx={{ color: issue.priority === "high" ? "#f44336" : "#ffa726", fontSize: 10, lineHeight: 1 }}
        >
          ●
        </Box>
        {issue.description}
      </Typography>
    ))}
  </Box>
);

export const IssueDotHeaderCell = ({ divider = false }: { divider?: boolean }) => (
  <TableCell
    sx={{
      width: 10,
      minWidth: 10,
      maxWidth: 10,
      p: 0,
      bgcolor: "action.selected",
      ...(divider ? { borderLeft: "1px solid", borderLeftColor: "divider" } : {}),
    }}
  />
);

export const IssueDotCell = ({ issues, rowSpan, divider = false }) => {
  const hasIssues = issues && issues.length > 0;
  const color = hasIssues ? (issues.some((i) => i.priority === "high") ? "error.main" : "warning.main") : undefined;
  return (
    <TableCell
      sx={{
        width: 10,
        minWidth: 10,
        maxWidth: 10,
        p: 0,
        textAlign: "center",
        ...(divider ? { borderLeft: "1px solid", borderLeftColor: "divider" } : {}),
      }}
      rowSpan={rowSpan}
    >
      {hasIssues && (
        <Box component="span" sx={{ fontSize: 8, lineHeight: 1, color, pl: "6px" }}>
          ●
        </Box>
      )}
    </TableCell>
  );
};

export const UnitNameAddr = ({ name, address, id, isVirtual, isConnected, isChanged, issues }) => {
  const classes = useStyles();
  const addr = address ?? (id || "").slice(-6);
  const hasIssues = issues && issues.length > 0;
  return (
    <Tooltip
      title={hasIssues ? <IssueTooltipContent issues={issues} /> : ""}
      placement="top-start"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 3,
            maxWidth: "unset",
            width: "max-content",
            p: "20px",
          },
        },
        arrow: { sx: { color: "background.paper" } },
      }}
    >
      <Box className={classes.unitNameAddrBox}>
        {isChanged && <Box component="span" className={classes.unitChangedDot} />}
        {name && (
          <Box component="span" title={name} className={classes.unitNameSpan}>
            {name}
          </Box>
        )}
        <Box
          component="span"
          className={classes.unitAddressSpan}
          sx={
            hasIssues ? { color: issues.some((i) => i.priority === "high") ? "error.main" : "warning.main" } : undefined
          }
        >
          ({addr})
        </Box>
        {isVirtual && <Chip label="virt" size="small" color="warning" className={classes.chipSmNoShrink} />}
      </Box>
    </Tooltip>
  );
};
